import { useState, useEffect, useRef, useCallback } from 'react';
import pdfjsLib from '../../utils/pdfjsClient';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import { SnackbarType } from '../Snackbar/types';
import { fetchVideoMaterials, fetchVideoMaterialContent } from '../../services/videoMaterialApi/videoMaterialApi';
import { downloadVideoMaterialOriginal } from '../../utils/videoMaterialDownload';
import { MATERIAL_DOWNLOAD_INTERNAL_ONLY } from '../../constants/accessMessages';
import { SessionManager } from '../../utils/SessionManager';
import { VideoMaterial, UseVideoMaterialViewerReturn } from './types';

/**
 * 【表示倍率の考え方】
 * `scale` state は pdf.js に渡す絶対倍率ではなく、「幅合わせ（フィット）＝1（100%）」を
 * 基準にした相対倍率にする。実際に pdf.js へ渡す倍率は、そのつど
 * `fitScale（枠の幅 ÷ ページ本来の幅）× scale` で求める（下の描画 effect 内）。
 * こうすることで、拡大率(%)の表示はそのまま「幅合わせ=100%」の意味になり、
 * 枠の幅が変わっても（ウインドウ幅変更・スマホの回転・全画面の出入り）fitScale だけを
 * 再計算すれば、縦横比を保ったまま追従できる。
 */
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;
const DEFAULT_SCALE = 1;

/**
 * canvas の実ピクセル数（width × height）の上限。iPad Safari の canvas 実装は
 * 約1678万画素（4096×4096相当）を超えると描画が白紙になることが知られており、
 * その値に合わせる。全画面表示・高DPR端末・横長ページ・拡大率300%を組み合わせると
 * 素直に devicePixelRatio をかけただけではこの上限を超えうるため、上限を超えるときは
 * outputScale（devicePixelRatio 側）だけを下げて画素数を上限内に収める。
 * 表示サイズ（canvas.style.width/height）と縦横比は変えない（画質だけが下がる）。
 */
const MAX_CANVAS_PIXELS = 16 * 1024 * 1024; // 16,777,216

/** requestFullscreen が使えない端末（iPhone Safari 等）かどうか */
const supportsFullscreenApi = (el: HTMLElement | null): boolean =>
  !!el && typeof el.requestFullscreen === 'function';

export const useVideoMaterialViewer = (videoId: string): UseVideoMaterialViewerReturn => {
  const showSnackbar = useSnackbar();
  const { isExternal } = useOriginAccess();

  const [materials, setMaterials] = useState<VideoMaterial[]>([]);
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);

  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(DEFAULT_SCALE);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isSavingOriginal, setIsSavingOriginal] = useState<boolean>(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // 幅合わせの基準にする枠（canvas の実の親、スクロール領域）
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null);
  // 描画中の pdf.js RenderTask。切り替え時に前の描画を必ずキャンセルしてから次を描く
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  // ウインドウ幅変更・スマホの回転・全画面の出入りのたびに +1 し、描画 effect に
  // 「枠の幅が変わったかもしれないので fitScale を計算し直せ」と伝えるだけのトリガー
  const [layoutVersion, setLayoutVersion] = useState<number>(0);
  // 直近の描画で実際に使った枠の幅。resize・全画面の出入りのたびに測り直し、これと
  // 違うときだけ layoutVersion を上げる（高さだけの変化や、幅が変わっていない
  // 再測定のたびに白く点滅しながら描き直すのを防ぐ）
  const lastMeasuredWrapperWidthRef = useRef<number>(0);

  // 一覧取得
  useEffect(() => {
    if (!videoId || videoId.startsWith('session_')) {
      setMaterials([]);
      return;
    }
    let cancelled = false;
    setIsLoadingList(true);
    fetchVideoMaterials(videoId)
      .then(list => {
        if (cancelled) return;
        setMaterials(list);
        if (list.length > 0) {
          setSelectedMaterialId(list[0].id);
        }
      })
      .catch(error => {
        console.error('資料一覧の取得に失敗しました:', error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [videoId]);

  const selectedMaterial = materials.find(m => m.id === selectedMaterialId) ?? null;

  /** pdf.js のドキュメントを破棄する（メモリ解放・古い資料の描画が後から追いつくのを防ぐ） */
  const destroyCurrentPdfDoc = useCallback((): void => {
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }
    if (pdfDocRef.current) {
      pdfDocRef.current.destroy?.();
      pdfDocRef.current = null;
    }
  }, []);

  // 選択中の資料のPDFを読み込む
  useEffect(() => {
    // 切り替え・選択解除のたびに、前の資料の状態を必ずリセットする（前の資料の
    // 表示が残ったまま次の資料に見えるのを防ぐ）。numPages を先に 0 に落とすことで、
    // 新旧のページ数が同じでも下の描画 effect が必ず再実行される。
    destroyCurrentPdfDoc();
    setNumPages(0);
    setCurrentPage(1);
    setScale(DEFAULT_SCALE);
    setLoadError(null);
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d');
      context?.clearRect(0, 0, canvas.width, canvas.height);
    }

    if (!selectedMaterial) {
      return;
    }

    let cancelled = false;
    setIsLoadingPdf(true);

    fetchVideoMaterialContent(selectedMaterial.id)
      .then(arrayBuffer => {
        if (cancelled) return null;
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        return loadingTask.promise;
      })
      .then(pdf => {
        if (!pdf) return;
        if (cancelled) {
          // レンダリング中に別の資料へ切り替わっていた場合はこのドキュメントを保持しない
          pdf.destroy?.();
          return;
        }
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
      })
      .catch(error => {
        console.error('資料の読み込みに失敗しました:', error);
        if (!cancelled) setLoadError('資料の読み込みに失敗しました');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPdf(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterial?.id]);

  // アンマウント時にも確実に破棄する
  useEffect(() => {
    return () => {
      destroyCurrentPdfDoc();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ページ・拡大率が変わるたびにcanvasへ描画
  useEffect(() => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || numPages === 0) return;

    let cancelled = false;

    pdf.getPage(currentPage).then((page: any) => {
      if (cancelled) return;

      // 前の描画タスクが残っていればキャンセルしてから次を描く（同じ canvas への
      // 二重描画・古いページが後から上書きされる事故を防ぐ）
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      // ページ本来のサイズ（scale=1）を基準に、枠の幅に合わせる倍率（fitScale）を求める。
      // 枠の幅を測れない場合（初回マウント直後でレイアウト未確定、テスト環境等）は
      // fitScale=1（等倍）にフォールバックする。
      const baseViewport = page.getViewport({ scale: 1 });
      const wrapperWidth = canvasWrapperRef.current?.clientWidth || 0;
      // resize 等が「前回この effect が実際に使った幅から変わったか」を比較できるよう、
      // ここで測った幅を基準値として記録しておく
      lastMeasuredWrapperWidthRef.current = wrapperWidth;
      const fitScale = wrapperWidth > 0 && baseViewport.width > 0
        ? wrapperWidth / baseViewport.width
        : 1;

      // 実際に描画する倍率 = 幅合わせ倍率 × 相対拡大率（scale state）。
      // width・height の両方をこの同じ倍率で求めるため、枠の幅が変わっても
      // 縦横比は常にページ本来の比率のまま保たれる（CSS 側で max-width 等の
      // 幅だけの制約をかけないことも必須。VideoMaterialViewer.module.css 参照）。
      const viewport = page.getViewport({ scale: fitScale * scale });
      const context = canvas.getContext('2d');
      if (!context) return;

      // devicePixelRatio を考慮する: canvas の実ピクセル数は端末の物理解像度に合わせ、
      // CSS 上の表示サイズは論理サイズのままにすることで、Retina 等でぼやけないようにする
      let outputScale = window.devicePixelRatio || 1;

      // 全画面・高DPR・拡大率が重なると、実ピクセル数が canvas の上限を超えて
      // 白紙になりうる（iPad Safari 等）。上限を超えるときは outputScale だけを
      // 下げて画素数を収める（表示サイズ・縦横比は viewport.width/height のまま変えない）
      const basePixelCount = viewport.width * viewport.height;
      if (basePixelCount > 0) {
        const projectedPixelCount = basePixelCount * outputScale * outputScale;
        if (projectedPixelCount > MAX_CANVAS_PIXELS) {
          outputScale = Math.sqrt(MAX_CANVAS_PIXELS / basePixelCount);
        }
      }

      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

      const renderTask = page.render({ canvasContext: context, viewport, transform });
      renderTaskRef.current = renderTask;
      renderTask.promise
        .catch((error: unknown) => {
          // cancel() による中断は正常系なのでログに出さない
          if ((error as { name?: string })?.name !== 'RenderingCancelledException') {
            console.error('資料ページの描画に失敗しました:', error);
          }
        })
        .finally(() => {
          if (renderTaskRef.current === renderTask) {
            renderTaskRef.current = null;
          }
        });
    });

    return () => {
      cancelled = true;
    };
  }, [currentPage, scale, numPages, layoutVersion]);

  // 枠の幅を測り直し、直近の描画で実際に使った幅（lastMeasuredWrapperWidthRef）から
  // 変わっていたときだけ layoutVersion を上げる。スマホでスクロールに伴い URL バーが
  // 出入りして「高さだけ」変わるような resize では描き直さない（白く点滅する事故を防ぐ）
  const remeasureLayoutIfChanged = useCallback((): void => {
    const width = canvasWrapperRef.current?.clientWidth || 0;
    if (width !== lastMeasuredWrapperWidthRef.current) {
      setLayoutVersion(prev => prev + 1);
    }
  }, []);

  // ウインドウ幅変更・スマホの回転（orientationchange。多くの端末では resize も伴うが、
  // 発火順序がブラウザ依存のため両方拾う）のたびに、間引きながら測り直す。
  // 短時間に連続発火しても requestAnimationFrame で1フレームに1回にまとめる
  useEffect(() => {
    let rafId: number | null = null;
    const scheduleRemeasure = (): void => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        remeasureLayoutIfChanged();
      });
    };
    window.addEventListener('resize', scheduleRemeasure);
    window.addEventListener('orientationchange', scheduleRemeasure);
    return () => {
      window.removeEventListener('resize', scheduleRemeasure);
      window.removeEventListener('orientationchange', scheduleRemeasure);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [remeasureLayoutIfChanged]);

  const selectMaterial = useCallback((id: number): void => {
    setSelectedMaterialId(id);
  }, []);

  const goToPrevPage = useCallback((): void => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback((): void => {
    setCurrentPage(prev => Math.min(numPages, prev + 1));
  }, [numPages]);

  const zoomIn = useCallback((): void => {
    setScale(prev => Math.min(MAX_SCALE, Number((prev + SCALE_STEP).toFixed(2))));
  }, []);

  const zoomOut = useCallback((): void => {
    setScale(prev => Math.max(MIN_SCALE, Number((prev - SCALE_STEP).toFixed(2))));
  }, []);

  // requestFullscreen が無い端末（iPhone Safari 等）では、ブラウザの全画面 API の代わりに
  // 画面いっぱいに広げる表示（CSS フォールバック）に切り替える
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState<boolean>(false);

  const toggleFullscreen = useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;

    if (!supportsFullscreenApi(el)) {
      setIsFallbackFullscreen(prev => !prev);
      return;
    }

    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(error => {
        console.error('全画面表示に失敗しました:', error);
      });
    } else {
      document.exitFullscreen?.().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const handleChange = (): void => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  // 全画面表示の出入り（実 Fullscreen API・iPhone Safari 等の CSS フォールバック双方）は
  // 枠の幅を変えうるため測り直す。実際に幅が変わっていなければ layoutVersion は上げない
  useEffect(() => {
    remeasureLayoutIfChanged();
  }, [isFullscreen, isFallbackFullscreen, remeasureLayoutIfChanged]);

  const handleSaveOriginal = useCallback((): void => {
    if (!selectedMaterial) return;
    if (isExternal) {
      showSnackbar(MATERIAL_DOWNLOAD_INTERNAL_ONLY, SnackbarType.ERROR);
      return;
    }

    const activeSession = SessionManager.getActiveSession();
    const token = activeSession ? activeSession.token : null;

    setIsSavingOriginal(true);
    downloadVideoMaterialOriginal(
      selectedMaterial.id,
      token || '',
      selectedMaterial.original_filename,
      () => setIsSavingOriginal(false),
      (errorMessage) => {
        setIsSavingOriginal(false);
        showSnackbar(errorMessage, SnackbarType.ERROR);
      }
    );
  }, [selectedMaterial, isExternal, showSnackbar]);

  return {
    materials,
    selectedMaterial,
    selectMaterial,
    isLoadingList,
    isLoadingPdf,
    loadError,
    canvasRef,
    containerRef,
    canvasWrapperRef,
    currentPage,
    numPages,
    goToPrevPage,
    goToNextPage,
    scale,
    zoomIn,
    zoomOut,
    isFullscreen: isFullscreen || isFallbackFullscreen,
    toggleFullscreen,
    isExternalNetwork: isExternal,
    isSavingOriginal,
    handleSaveOriginal,
  };
};
